import buildConfig from './build-config.json';
import { version } from '../package.json';
import { fetch } from './utility/fetch';
import { getRandInt } from './utility/random';

interface NotifyParams {
  error: Error | string;
  extraPayload: any;
  rollbarOptions?: {
    fingerprint?: string;
    [key: string]: any;
  };
  samplingRatio?: number;
}

const ROLLBAR_ITEM_API_URL: string = 'https://api.rollbar.com/api/1/item/';

type Opts = {
  appId: number;
  idfv: string;
  userAnnotation: string;
  isFirstSession: boolean;
};

type Stacktrace = { [key: string]: string };

export namespace ErrorNotifier {
  enum SendingStatus {
    BeforeInitialized,
    Enabled,
    Disabled,
  }
  const { environment, rollbarToken } = buildConfig;
  let status: SendingStatus = SendingStatus.BeforeInitialized;
  let buffer: NotifyParams[] = [];
  let idfv: Opts['idfv'];
  let userAnnotation: Opts['userAnnotation'];
  let appId: Opts['appId'];
  let isFirstSession: Opts['isFirstSession'];
  let context: string | null = null;

  export const setContext = (contextString: string): void => {
    context = contextString;
  };

  export const clearContext = (): void => {
    context = null;
  };

  export const notify = ({ error, extraPayload, rollbarOptions = {}, samplingRatio }: NotifyParams): void => {
    if (status === SendingStatus.BeforeInitialized) {
      buffer.push({ error, extraPayload, rollbarOptions, samplingRatio });
      return;
    }
    if (status === SendingStatus.Disabled) {
      return;
    }
    if (samplingRatio != null) {
      if (getRandInt(1, samplingRatio) !== 1) return;
    }

    let body = '';
    let name = '';
    let stacktrace: Stacktrace = {};
    // errorは文字列で渡される場合とErrorオブジェクトで渡される場合の2通りある
    // 文字列で渡されるとスタックトレースが取得できないので、できるだけErrorオブジェクトが渡されるようにしたい
    if (error instanceof Error) {
      // Error.stackが非標準の機能なのでundefinedの場合がある
      const stacktrace_or_undefined = error.stack;
      if (stacktrace_or_undefined !== undefined) {
        stacktrace = stacktrace_or_undefined
          .split('\n')
          .reduce((acc: Stacktrace, splitTrace: string, idx: number): Stacktrace => {
            acc[`stacktrace_${String.fromCharCode(65 + idx)}`] = splitTrace;
            return acc;
          }, {});
      }
      body = error.message;
      name = error.name;
    } else if (typeof error === 'string') {
      body = error;
    } else {
      return;
    }

    if (context != null) {
      body = `[${context}] ${body}`;
    }

    // See: https://explorer.docs.rollbar.com/#operation/create-item
    let data: FIXME = {
      environment,
      language: 'javascript',
      code_version: version,
      body: {
        message: {
          body,
          name,
          stacktrace,
          appId,
          idfv,
          isFirstSession,
          referrer: document.referrer,
          url: window.location.href,
          userAgent: window.navigator.userAgent,
          userAnnotation,
          extraPayload,
        },
      },
    };

    if (rollbarOptions.fingerprint != null) {
      data = {
        ...data,
        ...rollbarOptions,
      };
    }

    if (samplingRatio != null) {
      data.body.message.useSampling = true;
      data.body.message.samplingRatio = samplingRatio;
    }

    // See https://explorer.docs.rollbar.com/ for more details about the payload
    fetch(ROLLBAR_ITEM_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        access_token: rollbarToken,
        data,
      }),
      mode: 'cors',
    });
  };

  export const init = (isErrorNotified: boolean, options?: Opts): void => {
    if (!isErrorNotified) {
      status = SendingStatus.Disabled;
      buffer = [];
      return;
    }

    status = SendingStatus.Enabled;
    buffer.forEach((item) => {
      notify(item);
    });

    if (options) {
      idfv = options.idfv;
      userAnnotation = options.userAnnotation;
      appId = options.appId;
      isFirstSession = options.isFirstSession;
    }
  };
}
